import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { isFirmwareVersionInRange, UnitConverterId } from 'motion-master-client';
import { filter, map, withLatestFrom } from 'rxjs/operators';

import { setDeviceParametersInterfaceUnitsFromUiConfig, updateDeviceParameters } from './device-parameter.actions';
import { Device } from './device.model';
import { addSignalGeneratorParameters, updateSignalGeneratorParameters } from './signal-generator-parameter.actions';
import {
  SignalGeneratorParameter,
  signalGeneratorParameterId,
  signalGeneratorParametersMap,
} from './signal-generator-parameter.model';
import {
  createSignalGeneratorParameterByIdSelector,
  selectAllSignalGeneratorParameters,
} from './signal-generator-parameter.selectors';
import { addSignalGenerators } from './signal-generator.actions';
import { TuningController } from './tuning-controller.model';
import { createTuningControllerByIdSelector } from './tuning-controller.selectors';

import { AppState } from './index';

import { DeviceService } from '@app/services/device.service';
import { getCurrentValue, getInterfaceUnitFromUnitConverter } from '@app/util';

@Injectable()
export class SignalGeneratorParameterEffects {
  private actions$ = inject(Actions);
  private store$ = inject<Store<AppState>>(Store);
  private deviceService = inject(DeviceService);

  getInterfaceUnit(
    deviceSerialNumber: string,
    parameterUnit: string,
    originalUnit: string,
    unitConverterId: UnitConverterId,
  ) {
    const isConvertableUnit = ['position', 'velocity', 'acceleration'].includes(unitConverterId);
    let unit = originalUnit ?? parameterUnit;
    let UIUnit: string;

    if (isConvertableUnit) {
      const device = getCurrentValue(this.deviceService.selectDeviceByDeviceSerialNumber(deviceSerialNumber));
      const interfaceUnit = getCurrentValue(this.deviceService.selectDeviceUnitsForDropdown(device));
      unit = getInterfaceUnitFromUnitConverter(interfaceUnit, unitConverterId) ?? unit;
      UIUnit = interfaceUnit?.unit;
    }

    return unit ? { unit, interfaceUnit: UIUnit } : {};
  }

  addSignalGeneratorParametersOnAddSignalGenerators$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addSignalGenerators),
      withLatestFrom(this.store$),
      map(([action, state]) => {
        const signalGenerators = action.signalGenerators;
        const signalGeneratorParameters: SignalGeneratorParameter[] = [];
        signalGenerators.forEach((signalGenerator) => {
          const tuningController: TuningController = createTuningControllerByIdSelector(
            signalGenerator.tuningControllerId,
          ).projector(state.tuningControllers.entities);

          const firmwareVersion = this.deviceService.getDeviceParameterValue<string>(
            tuningController.deviceSerialNumber,
            'manufacturerSoftwareVersion',
          );
          const maxMotorSpeed = this.deviceService.getDeviceParameterValue(
            tuningController.deviceSerialNumber,
            'maxMotorSpeed',
          );

          const device: Device = { serialNumber: tuningController.deviceSerialNumber };

          const gearRatio = getCurrentValue(this.deviceService.selectDeviceGearRatio(device));
          const speed = Math.min(maxMotorSpeed, this.deviceService.convertDeviceRpmToSiUnitVelocityValue(device, 6000));

          let amplitude = Math.round(speed / 4);

          if (isFirmwareVersionInRange(firmwareVersion, '>=5.0.0-rc.5', '>=0.4.0')) {
            amplitude = Math.round(speed / 4 / gearRatio);
          } else {
            if (tuningController.type === 'position') {
              amplitude = Math.round(speed / 4);
            }
          }

          const acceleration = Math.round(
            this.deviceService.convertDeviceRpmToSiUnitVelocityValue(device, 2000) / gearRatio,
          );
          const deceleration = acceleration;

          signalGeneratorParametersMap
            .get(tuningController.type)
            .get(signalGenerator.type)
            .forEach((signalGeneratorParameterTemplate) => {
              const parameter = createSignalGeneratorParameterByIdSelector(
                signalGeneratorParameterId(signalGeneratorParameterTemplate, signalGenerator),
              ).projector(state.signalGeneratorParameters.entities);
              if (!parameter) {
                let signalGeneratorParameter = cloneDeep(signalGeneratorParameterTemplate);

                if (
                  tuningController.type === 'velocity' &&
                  (signalGeneratorParameter.fieldName === 'target' ||
                    signalGeneratorParameter.fieldName === 'amplitude')
                ) {
                  signalGeneratorParameter.value = amplitude;
                  signalGeneratorParameter.uintValue = amplitude;
                }

                if (signalGeneratorParameter.fieldName === 'profileVelocity') {
                  signalGeneratorParameter.value = amplitude;
                  signalGeneratorParameter.uintValue = amplitude;
                }

                if (signalGeneratorParameter.fieldName === 'profileAcceleration') {
                  signalGeneratorParameter.value = acceleration;
                  signalGeneratorParameter.uintValue = acceleration;
                }

                if (signalGeneratorParameter.fieldName === 'profileDeceleration') {
                  signalGeneratorParameter.value = deceleration;
                  signalGeneratorParameter.uintValue = deceleration;
                }

                const id = signalGeneratorParameterId(signalGeneratorParameter, signalGenerator);
                const signalGeneratorId = signalGenerator.id;
                const interfaceUnits = this.getInterfaceUnit(
                  device.serialNumber,
                  signalGeneratorParameter?.unit,
                  signalGeneratorParameter?.originalUnit,
                  signalGeneratorParameter?.unitConverterId,
                );

                signalGeneratorParameter = {
                  ...signalGeneratorParameter,
                  ...interfaceUnits,
                  id,
                  signalGeneratorId,
                };

                signalGeneratorParameters.push(signalGeneratorParameter as SignalGeneratorParameter);
              }
            });
        });

        return addSignalGeneratorParameters({ signalGeneratorParameters });
      }),
    ),
  );

  updateSignalGeneratorParametersOnUnitChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDeviceParameters),
      filter((action) => action.initiator === setDeviceParametersInterfaceUnitsFromUiConfig.type),
      withLatestFrom(this.store$.pipe(select(selectAllSignalGeneratorParameters))),
      map(([{ device }, parameters]) => {
        const updates = parameters
          .filter((parameter) => parameter?.unitConverterId)
          .reduce((acc, p) => {
            const { unit } = this.getInterfaceUnit(device.serialNumber, p.unit, p.originalUnit, p.unitConverterId);

            p.unit = unit;

            acc.push({
              update: { unit },
              id: p.id,
            });

            return acc;
          }, []);

        return updateSignalGeneratorParameters({
          updates,
        });
      }),
    ),
  );
}
